import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { TrendingUp, TrendingDown, Euro, Building2, BarChart3, FileText, Users, ArrowRightLeft, Wallet, Clock, CheckCircle2, XCircle, AlertCircle, Target, CreditCard } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line, ComposedChart, Area, AreaChart } from 'recharts';

const COLORS = ['#0F2B4E','#1A5FAB','#0B7A3E','#F28C00','#C0392B','#7B2FBE','#0891B2','#D97706','#64748B'];

const EUR = (v) => (v || 0).toLocaleString('pt-PT', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' EUR';
const PCT = (v) => (v || 0).toFixed(1) + '%';

function KPICard({ icon: Icon, label, value, sub, color, highlight }) {
  return (
    <div className="metric-card" style={highlight ? { borderLeft: `3px solid ${color || 'var(--accent)'}` } : {}}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={14} style={{ color: color || 'var(--accent)' }} />
        <span className="metric-label">{label}</span>
      </div>
      <p className="metric-value" style={{ color: color }}>{value}</p>
      {sub && <p className="text-[10px]" style={{ color: 'var(--text-light)' }}>{sub}</p>}
    </div>
  );
}

export default function FinancialPage() {
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  useEffect(() => { api.get('/financial/detailed').then(r => setData(r.data)).catch(() => {}); }, []);

  if (!data) return (
    <div className="animate-in pt-10 lg:pt-0">
      <div className="pro-card-padded p-8 text-center"><div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" /><p>A carregar dados financeiros...</p></div>
    </div>
  );

  const profitColor = (data.total_profit || 0) >= 0 ? 'var(--success)' : 'var(--error)';
  const globalResult = (data.total_profit || 0) - (data.employee_total_costs || 0);
  const globalColor = globalResult >= 0 ? 'var(--success)' : 'var(--error)';

  const tabs = [
    { id: 'overview', label: 'Visao Geral' },
    { id: 'cashflow', label: 'Cash Flow' },
    { id: 'projects', label: 'Projetos' },
    { id: 'payroll', label: 'Pessoal' },
    { id: 'pipeline', label: 'Pipeline' },
  ];

  const hasCashFlow = (data.cash_flow || []).length > 0;
  const hasCategoryData = data.costs_by_category?.length > 0;
  const projectChartData = (data.projects || []).slice(0,10).map(p => ({
    name: (p.name || '').length > 15 ? (p.name || '').slice(0, 15) + '…' : (p.name || ''),
    Orcamento: p.budget || 0, Gasto: p.spent || 0, Lucro: p.profit || 0
  }));

  const budgetStatusData = [
    { name: 'Aprovados', value: data.budget_by_status?.aprovado?.total || 0, count: data.budget_by_status?.aprovado?.count || 0, color: '#0B7A3E' },
    { name: 'Enviados', value: data.budget_by_status?.enviado?.total || 0, count: data.budget_by_status?.enviado?.count || 0, color: '#1A5FAB' },
    { name: 'Rascunho', value: data.budget_by_status?.rascunho?.total || 0, count: data.budget_by_status?.rascunho?.count || 0, color: '#64748B' },
    { name: 'Rejeitados', value: data.budget_by_status?.rejeitado?.total || 0, count: data.budget_by_status?.rejeitado?.count || 0, color: '#C0392B' },
  ].filter(d => d.value > 0 || d.count > 0);

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <div className="mb-5">
        <h1 className="text-xl font-bold">Area Financeira</h1>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Visao completa do negocio</p>
      </div>

      {/* TOP KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPICard icon={Euro} label="Total Orcamentado (Projetos)" value={EUR(data.total_budget)} color="var(--info)" />
        <KPICard icon={TrendingDown} label="Total Gasto (Projetos)" value={EUR(data.total_spent)} color="var(--error)" />
        <KPICard icon={TrendingUp} label="Lucro Bruto" value={EUR(data.total_profit)} color={profitColor} sub={`Margem: ${PCT(data.margin_pct)}`} />
        <KPICard icon={BarChart3} label="Resultado Liq. (- Pessoal)" value={EUR(globalResult)} color={globalColor} highlight sub={`Margem liq: ${PCT(data.total_budget ? globalResult/data.total_budget*100 : 0)}`} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPICard icon={FileText} label="Valor Orcamentos" value={EUR(data.total_budgets_value)} sub={`${data.approved_budgets || 0} aprov | ${data.pending_budgets || 0} pend | ${data.rejected_budgets || 0} rejeit`} color="#7B2FBE" />
        <KPICard icon={ArrowRightLeft} label="Taxa Conversao" value={PCT(data.conversion_rate)} sub="Orcamentos -> Obra" color="#0B7A3E" />
        <KPICard icon={Target} label="Pipeline Comercial" value={EUR(data.pipeline_value)} sub="Orcamentos aprovados/enviados" color="#F28C00" />
        <KPICard icon={Wallet} label="A Receber (Obras Ativas)" value={EUR(data.total_receivable)} sub="Saldo orcamentado de obras em curso" color="#0891B2" />
      </div>

      {/* TABS */}
      <div className="flex gap-0 mb-4 overflow-x-auto" style={{ borderBottom: '1px solid var(--border)' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`tab-btn ${activeTab === t.id ? 'active' : ''}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          {(hasCategoryData || projectChartData.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {hasCategoryData && (
                <div className="pro-card-padded" data-testid="category-chart">
                  <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Custos Reais por Categoria</h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={data.costs_by_category} cx="50%" cy="50%" outerRadius={90} innerRadius={40} dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {data.costs_by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => EUR(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
              {projectChartData.length > 0 && (
                <div className="pro-card-padded" data-testid="profitability-chart">
                  <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Rentabilidade por Projeto</h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={projectChartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 9 }} />
                      <YAxis tick={{ fontSize: 9 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                      <Tooltip formatter={(v) => EUR(v)} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Bar dataKey="Orcamento" fill="#1A5FAB" radius={[2,2,0,0]} />
                      <Bar dataKey="Gasto" fill="#C0392B" radius={[2,2,0,0]} />
                      <Bar dataKey="Lucro" fill="#0B7A3E" radius={[2,2,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {/* Summary boxes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="pro-card-padded">
              <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Projetos por Estado</h4>
              {Object.entries(data.projects_by_status || {}).map(([st, d]) => (
                <div key={st} className="flex items-center justify-between py-1" style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <span className={`badge ${{planeamento:'badge-info',em_curso:'badge-warning',concluido:'badge-success',suspenso:'badge-error'}[st] || 'badge-neutral'}`}>{st}</span>
                  <div className="text-right">
                    <span className="text-xs font-bold">{d.count} obras</span>
                    <span className="text-[10px] ml-2" style={{ color: 'var(--text-light)' }}>{EUR(d.budget)}</span>
                  </div>
                </div>
              ))}
              {!data.projects_by_status || Object.keys(data.projects_by_status).length === 0 ? <p className="text-xs" style={{ color: 'var(--text-light)' }}>Sem projetos</p> : null}
            </div>

            <div className="pro-card-padded">
              <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Orcamentos por Estado</h4>
              {budgetStatusData.map((b, i) => (
                <div key={i} className="flex items-center justify-between py-1" style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <span className="text-xs font-medium" style={{ color: b.color }}>● {b.name}</span>
                  <div className="text-right">
                    <span className="text-xs font-bold">{b.count} orc.</span>
                    <span className="text-[10px] ml-2" style={{ color: 'var(--text-light)' }}>{EUR(b.value)}</span>
                  </div>
                </div>
              ))}
              {budgetStatusData.length === 0 ? <p className="text-xs" style={{ color: 'var(--text-light)' }}>Sem orcamentos</p> : null}
            </div>

            <div className="pro-card-padded">
              <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Resumo Pessoal</h4>
              <div className="space-y-1">
                <div className="flex justify-between"><span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Salarios Pagos</span><span className="text-xs font-bold" style={{ color: 'var(--error)' }}>{EUR(data.total_salaries)}</span></div>
                <div className="flex justify-between"><span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Adiantamentos</span><span className="text-xs font-bold" style={{ color: 'var(--error)' }}>{EUR(data.total_advances)}</span></div>
                <div className="flex justify-between"><span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Despesas</span><span className="text-xs font-bold" style={{ color: 'var(--error)' }}>{EUR(data.total_expenses)}</span></div>
                <div className="flex justify-between pt-1" style={{ borderTop: '1px solid var(--border)' }}>
                  <span className="text-xs font-semibold">Total Pessoal</span>
                  <span className="text-xs font-bold" style={{ color: 'var(--error)' }}>{EUR(data.employee_total_costs)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CASH FLOW TAB */}
      {activeTab === 'cashflow' && (
        <div className="space-y-4">
          {hasCashFlow ? (
            <>
              <div className="pro-card-padded">
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-secondary)' }}>Cash Flow Mensal (Receitas vs Custos vs Salarios)</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={data.cash_flow} margin={{ top: 5, right: 10, left: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                    <YAxis tick={{ fontSize: 9 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <Tooltip formatter={(v) => EUR(v)} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar dataKey="receitas" name="Receitas" fill="#0B7A3E" radius={[2,2,0,0]} />
                    <Bar dataKey="custos" name="Custos Obra" fill="#C0392B" radius={[2,2,0,0]} />
                    <Bar dataKey="salarios" name="Salarios" fill="#F28C00" radius={[2,2,0,0]} />
                    <Line type="monotone" dataKey="saldo" name="Saldo" stroke="#0F2B4E" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div className="pro-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="pro-table">
                    <thead><tr><th>Mes</th><th>Receitas</th><th>Custos Obra</th><th>Salarios</th><th>Saldo</th></tr></thead>
                    <tbody>
                      {data.cash_flow.map((r, i) => (
                        <tr key={i}>
                          <td className="font-medium">{r.month}</td>
                          <td style={{ color: 'var(--success)' }}>{EUR(r.receitas)}</td>
                          <td style={{ color: 'var(--error)' }}>{EUR(r.custos)}</td>
                          <td style={{ color: '#F28C00' }}>{EUR(r.salarios)}</td>
                          <td className="font-bold" style={{ color: r.saldo >= 0 ? 'var(--success)' : 'var(--error)' }}>{EUR(r.saldo)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="pro-card-padded text-center py-12">
              <BarChart3 size={40} className="mx-auto mb-3" style={{ color: 'var(--text-light)' }} />
              <p className="font-semibold">Sem dados de cash flow</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Registe custos com datas e complete projetos para ver o cash flow</p>
            </div>
          )}
        </div>
      )}

      {/* PROJECTS TAB */}
      {activeTab === 'projects' && (
        <div className="space-y-4">
          {data.projects?.length > 0 ? (
            <div className="pro-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="pro-table" data-testid="financial-table">
                  <thead><tr><th>Projeto</th><th>Estado</th><th>Orcamento</th><th>Gasto</th><th>Lucro</th><th>Margem</th></tr></thead>
                  <tbody>
                    {data.projects.map(p => (
                      <tr key={p.id}>
                        <td className="font-medium">{p.name}</td>
                        <td><span className={`badge ${{concluido:'badge-success',em_curso:'badge-warning',planeamento:'badge-info',suspenso:'badge-error'}[p.status] || 'badge-neutral'}`}>{p.status}</span></td>
                        <td>{EUR(p.budget)}</td>
                        <td style={{ color: 'var(--error)' }}>{EUR(p.spent)}</td>
                        <td className="font-semibold" style={{ color: p.profit >= 0 ? 'var(--success)' : 'var(--error)' }}>{EUR(p.profit)}</td>
                        <td><span className={`badge ${p.margin >= 15 ? 'badge-success' : p.margin >= 0 ? 'badge-warning' : 'badge-error'}`}>{PCT(p.margin)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="pro-card-padded text-center py-8"><Building2 size={40} className="mx-auto mb-3" style={{ color: 'var(--text-light)' }} /><p className="font-semibold">Sem projetos</p></div>
          )}
        </div>
      )}

      {/* PAYROLL TAB */}
      {activeTab === 'payroll' && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <KPICard icon={Users} label="Total Salarios" value={EUR(data.total_salaries)} color="var(--error)" />
            <KPICard icon={CreditCard} label="Adiantamentos" value={EUR(data.total_advances)} color="#F28C00" />
            <KPICard icon={Wallet} label="Despesas" value={EUR(data.total_expenses)} color="#7B2FBE" />
          </div>
          {(data.salary_by_employee || []).length > 0 ? (
            <div className="pro-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="pro-table">
                  <thead><tr><th>Funcionario</th><th>Salario Mensal</th><th>Total Pago</th><th>Adiantamentos</th><th>Meses em Atraso</th></tr></thead>
                  <tbody>
                    {data.salary_by_employee.map((e, i) => (
                      <tr key={i}>
                        <td className="font-medium">{e.name}</td>
                        <td>{EUR(e.monthly_salary)}</td>
                        <td style={{ color: 'var(--error)' }}>{EUR(e.total_paid)}</td>
                        <td style={{ color: '#F28C00' }}>{EUR(e.advances)}</td>
                        <td>
                          {e.unpaid_months > 0 ? (
                            <span className="badge badge-error">{e.unpaid_months} mes(es)</span>
                          ) : (
                            <span className="badge badge-success">Em dia</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="pro-card-padded text-center py-8"><Users size={40} className="mx-auto mb-3" style={{ color: 'var(--text-light)' }} /><p className="font-semibold">Sem dados de pessoal</p><p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Registe salarios na pagina de Funcionarios</p></div>
          )}
        </div>
      )}

      {/* PIPELINE TAB */}
      {activeTab === 'pipeline' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {budgetStatusData.map((b, i) => (
              <div key={i} className="pro-card-padded text-center" style={{ borderTop: `3px solid ${b.color}` }}>
                <p className="text-xs font-medium mb-1" style={{ color: b.color }}>{b.name}</p>
                <p className="text-lg font-bold" style={{ color: b.color }}>{b.count}</p>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{EUR(b.value)}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="pro-card-padded">
              <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Distribuicao de Orcamentos</h4>
              {budgetStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={budgetStatusData} cx="50%" cy="50%" outerRadius={80} dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                      {budgetStatusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => EUR(v)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-center py-8" style={{ color: 'var(--text-light)' }}>Sem dados</p>}
            </div>
            <div className="pro-card-padded">
              <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Metricas Comerciais</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-2 rounded-md" style={{ background: 'var(--surface-alt)' }}>
                  <span className="text-xs">Taxa de Conversao</span>
                  <span className="text-sm font-bold" style={{ color: 'var(--success)' }}>{PCT(data.conversion_rate)}</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded-md" style={{ background: 'var(--surface-alt)' }}>
                  <span className="text-xs">Pipeline (Aprovados + Enviados)</span>
                  <span className="text-sm font-bold" style={{ color: '#F28C00' }}>{EUR(data.pipeline_value)}</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded-md" style={{ background: 'var(--surface-alt)' }}>
                  <span className="text-xs">Valor Total Orcamentado</span>
                  <span className="text-sm font-bold">{EUR(data.total_budgets_value)}</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded-md" style={{ background: 'var(--surface-alt)' }}>
                  <span className="text-xs">Orcamentos em Rascunho</span>
                  <span className="text-sm font-bold" style={{ color: '#64748B' }}>{data.pending_budgets || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
